import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { mockQuotationComparisonService } from '@/services/mock/MockQuotationComparisonService';
import { mockDocumentCommentService } from '@/services/mock/MockDocumentCommentService';
import { AiRecommendationInsight, QuotationExceptionItem, QuotationRecord, SupplierSelectionPayload } from '@/types/domain/quotation-comparison';
import { DocumentAttachment, RequestComment } from '@/types/domain';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { formatInr } from '@/lib/utils';
import { animateFadeIn } from '@/lib/motion';
import { ROUTES } from '@/constants/routes';
import {
  ArrowLeft,
  FileText,
  MessageSquare,
  Sparkles,
  Trophy,
  Download,
  ExternalLink,
  Layers,
} from 'lucide-react';
import { QuotationMatrixTable } from '@/components/quotations/QuotationMatrixTable';
import { AiRecommendationCard } from '@/components/quotations/AiRecommendationCard';
import { QuotationExceptionCenter } from '@/components/quotations/QuotationExceptionCenter';
import { QuotationSelectionModal } from '@/components/quotations/QuotationSelectionModal';
import { DocumentManagementPanel } from '@/components/data-display/DocumentManagementPanel';
import { EnterpriseCommentThread } from '@/components/data-display/EnterpriseCommentThread';

export interface QuotationComparisonPageProps {
  rfqIdProp?: string;
  isDrawer?: boolean;
  onClose?: () => void;
}

export const QuotationComparisonPage: React.FC<QuotationComparisonPageProps> = ({
  rfqIdProp,
  isDrawer,
}) => {
  const { rfqId: paramRfqId } = useParams<{ rfqId: string }>();
  const rfqId = rfqIdProp || paramRfqId;
  const navigate = useNavigate();
  const pageRef = useRef<HTMLDivElement>(null);

  const [quotations, setQuotations] = useState<QuotationRecord[]>([]);
  const [aiInsight, setAiInsight] = useState<AiRecommendationInsight | null>(null);
  const [exceptions, setExceptions] = useState<QuotationExceptionItem[]>([]);
  const [documents, setDocuments] = useState<DocumentAttachment[]>([]);
  const [comments, setComments] = useState<RequestComment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  // Selection Modal State
  const [selectedQuotationForAward, setSelectedQuotationForAward] = useState<QuotationRecord | null>(null);
  const [isSelectionModalOpen, setIsSelectionModalOpen] = useState(false);
  const [isSubmittingSelection, setIsSubmittingSelection] = useState(false);
  const [poHandoffPayload, setPoHandoffPayload] = useState<SupplierSelectionPayload | null>(null);

  const [activeTab, setActiveTab] = useState<'MATRIX' | 'AI_INSIGHT' | 'EXCEPTIONS' | 'DOCUMENTS' | 'COMMENTS'>('MATRIX');

  const loadData = async () => {
    if (!rfqId) return;
    try {
      setIsLoading(true);
      const [quotes, ai, excs, docs, comms] = await Promise.all([
        mockQuotationComparisonService.getQuotationsForRfq(rfqId),
        mockQuotationComparisonService.getAiRecommendation(rfqId),
        mockQuotationComparisonService.getExceptions(rfqId),
        mockDocumentCommentService.getDocuments('RFQ', rfqId),
        mockDocumentCommentService.getComments('RFQ', rfqId),
      ]);

      setQuotations(quotes);
      setAiInsight(ai);
      setExceptions(excs);
      setDocuments(docs);
      setComments(comms);

      // Check if supplier already selected
      const selected = quotes.find((q) => q.isSelected);
      if (selected) {
        setPoHandoffPayload({
          rfqId,
          rfqNumber: selected.rfqNumber,
          selectedQuotationId: selected.id,
          selectedVendorId: selected.vendorId,
          selectedVendorName: selected.vendorName,
          evaluatedTotalAmount: selected.evaluatedCost,
          currency: 'INR',
          isAiRecommended: selected.id === ai.recommendedQuotationId,
          justificationReason: selected.selectionJustification || 'Selected via Strategic Sourcing Matrix',
          selectedAt: new Date().toISOString(),
          selectedBy: 'David Sterling (Procurement Manager)',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    animateFadeIn(pageRef.current);
    loadData();
  }, [rfqId]);

  const handleOpenSelectionModal = (quote: QuotationRecord) => {
    setSelectedQuotationForAward(quote);
    setIsSelectionModalOpen(true);
  };

  const handleConfirmAwardSelection = async (justification: string) => {
    if (!rfqId || !selectedQuotationForAward) return;
    try {
      setIsSubmittingSelection(true);
      const payload = await mockQuotationComparisonService.selectSupplier(
        rfqId,
        selectedQuotationForAward.id,
        justification
      );
      setPoHandoffPayload(payload);
      setIsSelectionModalOpen(false);
      await loadData();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to select supplier');
    } finally {
      setIsSubmittingSelection(false);
    }
  };

  const handleExportMatrix = () => {
    setIsExporting(true);
    // Real CSV download simulation
    const headers = ['Evaluation Parameter', ...quotations.map((q) => `${q.vendorName} (${q.quotationNumber})`)];
    const rows = [
      ['Base Quoted Subtotal', ...quotations.map((q) => q.subtotalAmount.toString())],
      ['Discount Amount', ...quotations.map((q) => q.discountAmount.toString())],
      ['Freight Charges', ...quotations.map((q) => q.freightAmount.toString())],
      ['Taxes (GST)', ...quotations.map((q) => q.taxAmount.toString())],
      ['Total Evaluated Cost (INR)', ...quotations.map((q) => q.evaluatedCost.toString())],
      ['Overall Score', ...quotations.map((q) => `${q.overallScore}/100`)],
      ['Payment Terms', ...quotations.map((q) => q.commercialTerms.paymentTerms)],
      ['Incoterms', ...quotations.map((q) => q.commercialTerms.incoterms)],
    ];
    const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `Quotation_Matrix_${rfqId || 'RFQ'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => setIsExporting(false), 800);
  };

  if (isLoading) {
    return (
      <div className="p-16 text-center text-xs text-foreground-secondary font-medium font-poppins">
        Loading Multi-Vendor Quotation Matrix & Evaluation Dossier...
      </div>
    );
  }

  const winningQuote = quotations.find((q) => q.isSelected);
  const lowestEvaluatedCost = quotations.length > 0 ? Math.min(...quotations.map((q) => q.evaluatedCost)) : 0;

  return (
    <div ref={pageRef} className={`space-y-4 font-poppins ${isDrawer ? 'pb-2' : 'pb-24 max-w-7xl mx-auto'}`}>
      {/* Top Header Bar */}
      <div className={`flex justify-between gap-3 border-b border-border/60 pb-4 ${isDrawer ? 'flex-col items-start' : 'flex-col sm:flex-row sm:items-center'}`}>
        <div className="flex items-center gap-3">
          {!isDrawer && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(ROUTES.RFQ.DETAILS(rfqId || 'RFQ-2001'))}
              className="h-9 rounded-xl border-border font-bold font-poppins cursor-pointer"
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to RFQ Workspace
            </Button>
          )}

          <div className="flex items-center gap-2">
            <span className="font-poppins text-xl font-extrabold text-foreground tracking-tight">
              Sourcing RFQ: {rfqId}
            </span>
            <span className="font-mono text-xs font-bold text-primary bg-primary/10 px-2.5 py-0.5 rounded-lg border border-primary/20">
              {quotations.length} Bids Submitted
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportMatrix}
            disabled={isExporting}
            className="rounded-xl font-bold font-poppins cursor-pointer border-border"
          >
            <Download className="h-3.5 w-3.5 mr-1.5" />
            {isExporting ? 'Exporting...' : 'Export Matrix (CSV)'}
          </Button>

          {poHandoffPayload && (
            <Button
              size="sm"
              onClick={() =>
                navigate(ROUTES.PURCHASE_ORDERS.NEW, {
                  state: { supplierSelectionPayload: poHandoffPayload },
                })
              }
              className="rounded-xl font-bold bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs font-poppins cursor-pointer"
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Generate Purchase Order (PO) →
            </Button>
          )}
        </div>
      </div>

      {/* Main Sourcing Header Card */}
      <Card className="border-border rounded-2xl shadow-xs font-poppins">
        <CardContent className="p-6 space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-border/60 pb-4">
            <div className="space-y-1">
              <span className="text-xs font-extrabold text-primary uppercase tracking-wider">
                Strategic Sourcing Matrix Evaluation
              </span>
              <h1 className="font-poppins text-2xl font-extrabold text-foreground">
                Side-by-Side Commercial & Technical Quotation Matrix
              </h1>
              <p className="text-xs text-foreground-secondary font-medium">
                Comprehensive evaluation of unit prices, statutory GST taxes, freight incoterms, warranty terms, and technical compliance across pre-qualified suppliers.
              </p>
            </div>

            <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-4 text-right space-y-1 min-w-[220px]">
              <span className="text-[11px] font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider">
                Lowest Evaluated TCO (L1)
              </span>
              <span className="font-poppins text-2xl font-extrabold text-emerald-600 dark:text-emerald-400 font-mono block">
                {formatInr(lowestEvaluatedCost)}
              </span>
            </div>
          </div>

          {winningQuote && (
            <div className="p-4 rounded-xl border border-emerald-500/30 bg-emerald-500/10 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
              <div className="flex items-center gap-2 font-bold text-emerald-800 dark:text-emerald-300">
                <Trophy className="h-5 w-5 text-amber-500 fill-current shrink-0" />
                <span>
                  Awarded Commercial Sanction: <strong className="text-foreground">{winningQuote.vendorName}</strong> ({winningQuote.quotationNumber})
                </span>
              </div>
              <span className="font-mono text-emerald-700 dark:text-emerald-400 font-extrabold text-sm">
                Sanctioned TCO: {formatInr(winningQuote.evaluatedCost)}
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* AI Recommendation Insight */}
      {aiInsight && <AiRecommendationCard insight={aiInsight} />}

      {/* Navigation Tabs */}
      <div className="flex items-center gap-1.5 p-1 rounded-2xl bg-muted/40 border border-border/80 overflow-x-auto custom-scrollbar no-scrollbar">
        <button
          onClick={() => setActiveTab('MATRIX')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap font-poppins ${
            activeTab === 'MATRIX'
              ? 'bg-primary text-primary-foreground shadow-xs'
              : 'text-foreground-secondary hover:bg-muted hover:text-foreground'
          }`}
        >
          <Layers className="h-4 w-4" /> Comparison Matrix ({quotations.length})
        </button>

        <button
          onClick={() => setActiveTab('EXCEPTIONS')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap font-poppins ${
            activeTab === 'EXCEPTIONS'
              ? 'bg-primary text-primary-foreground shadow-xs'
              : 'text-foreground-secondary hover:bg-muted hover:text-foreground'
          }`}
        >
          <Sparkles className="h-4 w-4" /> Exceptions & Risk ({exceptions.length})
        </button>

        <button
          onClick={() => setActiveTab('DOCUMENTS')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap font-poppins ${
            activeTab === 'DOCUMENTS'
              ? 'bg-primary text-primary-foreground shadow-xs'
              : 'text-foreground-secondary hover:bg-muted hover:text-foreground'
          }`}
        >
          <FileText className="h-4 w-4" /> Attachments ({documents.length})
        </button>

        <button
          onClick={() => setActiveTab('COMMENTS')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap font-poppins ${
            activeTab === 'COMMENTS'
              ? 'bg-primary text-primary-foreground shadow-xs'
              : 'text-foreground-secondary hover:bg-muted hover:text-foreground'
          }`}
        >
          <MessageSquare className="h-4 w-4" /> Activity & Discussions ({comments.length})
        </button>
      </div>

      {/* TAB 1: COMPARISON MATRIX */}
      {activeTab === 'MATRIX' && (
        <QuotationMatrixTable quotations={quotations} onSelectSupplier={handleOpenSelectionModal} />
      )}

      {/* TAB 2: EXCEPTIONS */}
      {activeTab === 'EXCEPTIONS' && <QuotationExceptionCenter exceptions={exceptions} />}

      {/* TAB 3: DOCUMENTS */}
      {activeTab === 'DOCUMENTS' && (
        <DocumentManagementPanel
          documents={documents}
          onUploadFile={async (file, cat) => {
            await mockDocumentCommentService.uploadDocument('RFQ', rfqId || 'RFQ-2001', file, cat);
            loadData();
          }}
          onDownloadFile={async (docId) => {
            const res = await mockDocumentCommentService.downloadDocument(docId);
            window.open(res.downloadUrl, '_blank');
          }}
          onDeleteFile={async (docId) => {
            await mockDocumentCommentService.deleteDocument(docId);
            loadData();
          }}
        />
      )}

      {/* TAB 4: COMMENTS */}
      {activeTab === 'COMMENTS' && (
        <div className="max-w-3xl">
          <EnterpriseCommentThread
            comments={comments}
            attachments={documents}
            onAddComment={async (text, attIds) => {
              await mockDocumentCommentService.createComment('RFQ', rfqId || 'RFQ-2001', text, attIds);
              loadData();
            }}
            onUpdateComment={async (commentId, text) => {
              await mockDocumentCommentService.updateComment(commentId, text);
              loadData();
            }}
            onDeleteComment={async (commentId) => {
              await mockDocumentCommentService.deleteComment(commentId);
              loadData();
            }}
          />
        </div>
      )}

      {/* Selection Justification Modal */}
      {selectedQuotationForAward && (
        <QuotationSelectionModal
          isOpen={isSelectionModalOpen}
          onClose={() => setIsSelectionModalOpen(false)}
          onConfirmSelection={handleConfirmAwardSelection}
          selectedQuotation={selectedQuotationForAward}
          aiInsight={aiInsight}
          isSubmitting={isSubmittingSelection}
        />
      )}
    </div>
  );
};
